import type { ErrorEvent } from '@sentry/core';
import { type AxiosError, isAxiosError } from 'axios';

import type { IAxiosErrorContext } from './types';

/**
 * Recursively adds Axios error context to Sentry event
 * Handles nested errors via error.cause chain
 * @see https://github.com/getsentry/sentry-javascript/issues/4377
 */
export function addAxiosContextRecursive(
	event: ErrorEvent,
	error: unknown
): void {
	if (isAxiosError(error)) {
		addAxiosContext(event, error);
	} else if (error instanceof Error && 'cause' in error && error.cause) {
		addAxiosContextRecursive(event, error.cause);
	}
}

/**
 * Adds Axios response data to Sentry event contexts
 */
function addAxiosContext(event: ErrorEvent, error: AxiosError): void {
	if (!error.response) return;

	const contexts: IAxiosErrorContext = {
		Axios: {
			Response: {
				status: error.response.status,
				statusText: error.response.statusText,
				data: error.response.data,
			},
		},
	};

	event.contexts = { ...event.contexts, ...contexts };
}

/**
 * Enriches Sentry event with API endpoint tags and fingerprints
 * - Adds tags for filtering (api.endpoint, api.method, api.status)
 * - Sets custom fingerprint for better grouping by endpoint
 * - Improves error message title
 */
export function enrichAxiosError(event: ErrorEvent, error: AxiosError): void {
	const url = error.config?.url;
	const method = error.config?.method;
	const status = error.response?.status;

	if (!url) return;

	/** Extract pathname from URL */
	const urlObj = new URL(url, 'http://dummy.com');
	const pathname = urlObj.pathname;

	/** Extract error code from response data */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const responseData = error.response?.data as any;
	const errorCode = responseData?.code;

	/** Add tags for filtering in Sentry UI */
	event.tags = {
		...event.tags,
		'api.endpoint': pathname,
		'api.method': method,
		'api.status': status,
		...(errorCode && { 'api.error.code': errorCode }),
	};

	/** Custom fingerprint groups errors by endpoint + status */
	event.fingerprint = ['{{ default }}', pathname, String(status)];

	/** Improve error title for better readability */
	if (event.exception?.values?.[0]) {
		event.exception.values[0].value = `Request failed with status code ${status}: ${method?.toUpperCase()} ${pathname}`;
	}

	const requestContext: IAxiosErrorContext['Axios']['Request'] = {};

	/** Add request headers for debugging */
	const headers = error.config?.headers;

	if (headers) {
		const contentVersion = headers.get('Content-Version');
		const accept = headers.getAccept();
		const contentType = headers.getContentType();

		requestContext.headers = {
			...(contentVersion && { 'Content-Version': String(contentVersion) }),
			...(accept && { Accept: String(accept) }),
			...(contentType && { 'Content-Type': String(contentType) }),
		};
	}

	event.contexts = {
		...event.contexts,
		Axios: {
			...(event.contexts?.['Axios'] as IAxiosErrorContext['Axios']),
			Request: requestContext,
		},
	};
}
